export const trendDirectionEnums = {
    IMPROVING: 'IMPROVING',
    DECLINING: 'DECLINING',
    STABLE: 'STABLE',
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export const buildAverageLine = ({ values = [], decimals = 1 }) => {
    const validValues = (Array.isArray(values) ? values : [])
        .filter((value) => value !== null && value !== undefined)
        .map(Number)
        .filter((value) => !Number.isNaN(value));

    if (validValues.length < 2) {
        return { value: null };
    }

    const average = validValues.reduce((sum, value) => sum + value, 0) / validValues.length;

    return { value: Number(average.toFixed(decimals)) };
};

export const buildLinearTrendLine = ({ values = [], maxValue = 100, decimals = 1 }) => {
    if (!Array.isArray(values) || values.length < 2) {
        return {
            seriesData: [],
            slope: 0,
            direction: trendDirectionEnums.STABLE,
        };
    }

    const validPoints = values
        .map((value, index) => ({ value, index }))
        .filter((point) => point.value !== null && point.value !== undefined);

    if (validPoints.length < 3) {
        return {
            seriesData: [],
            slope: 0,
            direction: trendDirectionEnums.STABLE,
        };
    }

    const uniqueValues = new Set(validPoints.map((point) => Number(point.value)));
    if (uniqueValues.size <= 1) {
        return {
            seriesData: [],
            slope: 0,
            direction: trendDirectionEnums.STABLE,
        };
    }

    const xMean = validPoints.reduce((sum, point) => sum + point.index, 0) / validPoints.length;
    const yMean = validPoints.reduce((sum, point) => sum + point.value, 0) / validPoints.length;

    let numerator = 0;
    let denominator = 0;

    validPoints.forEach((point) => {
        numerator += (point.index - xMean) * (point.value - yMean);
        denominator += (point.index - xMean) ** 2;
    });

    const slope = denominator ? numerator / denominator : 0;
    const intercept = yMean - slope * xMean;
    const stabilityThreshold = maxValue <= 0 ? 0.01 : maxValue * 0.0035;
    const firstValidIndex = validPoints[0].index;
    const lastValidIndex = validPoints[validPoints.length - 1].index;

    const direction =
        Math.abs(slope) <= stabilityThreshold
            ? trendDirectionEnums.STABLE
            : slope > 0
              ? trendDirectionEnums.IMPROVING
              : trendDirectionEnums.DECLINING;

    return {
        slope,
        direction,
        seriesData: values.map((_, index) => {
            if (index < firstValidIndex || index > lastValidIndex) {
                return null;
            }
            const projected = intercept + slope * index;
            return Number(clamp(projected, 0, maxValue).toFixed(decimals));
        }),
    };
};
